import { customElement, property } from 'lit/decorators.js';
import { style } from '@/components/radio/radio.styles';
import { html, LitElement, PropertyValues } from "lit";
import { FormValidationMixin } from "@/components/mixins/form-validation-mixin";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { FormControlDependencyMixin } from "@/components/form-control-dependency/form-control-dependency-mixin";

@customElement('sbk-radio')
export class SbkRadio extends FormControlDependencyMixin(FormValidationMixin(LitElement)) {

    static formAssociated = true;

    static shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true };

    _internals: ElementInternals;

    @property()
    label: string = ''

    @property({reflect: true})
    name: string = ''

    @property()
    value: string = ''

    @property({ type: Boolean })
    disabled = false

    @property({ type: Boolean, reflect: true })
    checked = false;

    @property({ type: Boolean })
    invalid = false;

    static get styles() {
        return [
            style
        ];
    }

    constructor() {
        super();
        this._internals = this.attachInternals();
        this.addEventListener('form-control-dependency-show', () => {
            this.show();
            this.setValue();
        });
        this.addEventListener('form-control-dependency-hide', () => {
            this.hide();
            this.resetValue();
        });
        this.addEventListener('form-control-dependency-required', () => {
            this._required();
        });
        this.addEventListener('form-control-dependency-optional', () => {
            this.optional();
        });
    }

    connectedCallback() {
        super.connectedCallback();
        if (this.checked) {
            this.setValue();
        } else {
            this.resetValue();
        }
        if (!this.hasAttribute('role')) {
            this.setAttribute('role', 'radio');
        }
    }

    willUpdate(changedProperties: PropertyValues<this>) {
        if (changedProperties.has('checked')) {
            if (this.checked) {
                this.setValue();
            } else {
                this.resetValue();
            }
        }
    }

    render() {
        return html`
            <div class="radio__wrapper${this.invalid ? ' radio__wrapper--error' : ''}${this.disabled ? ' radio__wrapper--disabled' : ''}" @click=${this._handleChange}>
                <input
                    id=${this.id}
                    class="radio__icon"
                    type="radio"
                    .value=${this.value}
                    name=${this.name}
                    ?disabled=${this.disabled}
                    ?checked=${this.checked}
                >
                <label for=${this.id} class="radio__label">
                    ${unsafeHTML(this.label)}
                </label>
            </div>
        `;
    }

    _handleChange = (event: Event) => {
        event.preventDefault();
        const element = <HTMLInputElement>event.target;
        this.setValue();
        this.updateComplete.then(() =>{
            const inputChangeEvent = new CustomEvent('radio-change', {
                detail: { value: this.value },
                bubbles: true
            });
            this.dispatchEvent(inputChangeEvent);
            element.blur();
        });
    }

    hide(): void {
        this.hidden = true;
        this.invalid = false;
    }

    show(): void {
        this.hidden = false;
    }

    resetValue(): void {
        this.checked = false;
        this._internals.setFormValue(null, null);
    }

    setValue(): void {
        if (this.checked) {
            this._internals.setFormValue(this.value, 'checked');
        }
    }

    _required(): void {
        this.setAttribute('required', 'required');
    }

    optional(): void {
        this.removeAttribute('required');
        this.invalid = false;
    }
}
